<script>
// <el-col> uses different breakpoints than ours
// <grid-col> is an <el-col> adapted with our breakpoints
// <grid-col> is a temporary code-redundancy until we override element-ui globally
export default {
  name: 'GridCol',
  props: {
    span: {
      type: Number,
      default: 24,
    },
    tag: {
      type: String,
      default: 'div',
    },
    offset: {
      type: Number,
      default: 0,
    },
    pull: {
      type: Number,
      default: 0,
    },
    push: {
      type: Number,
      default: 0,
    },
    xs: {
      type: [Number, Object],
      default: () => {},
    },
    sm: {
      type: [Number, Object],
      default: () => {},
    },
    md: {
      type: [Number, Object],
      default: () => {},
    },
    lg: {
      type: [Number, Object],
      default: () => {},
    },
    xl: {
      type: [Number, Object],
      default: () => {},
    },
  },
  computed: {
    gutter() {
      let parent = this.$parent;
      while (parent && parent.$options.componentName !== 'ElRow') {
        parent = parent.$parent;
      }
      return parent ? parent.gutter : 0;
    },
  },
  render(h) {
    const classList = [];
    const style = {};

    if (this.gutter) {
      style.paddingLeft = `${this.gutter / 2}px`;
      style.paddingRight = style.paddingLeft;
    }

    ['span', 'offset', 'pull', 'push'].forEach((prop) => {
      if (this[prop] || this[prop] === 0) {
        classList.push(
          prop !== 'span'
            ? `alt-col-${prop}-${this[prop]}`
            : `alt-col-${this[prop]}`,
        );
      }
    });

    ['xs', 'sm', 'md', 'lg', 'xl'].forEach((size) => {
      if (typeof this[size] === 'number') {
        classList.push(`alt-col-${size}-${this[size]}`);
      } else if (typeof this[size] === 'object') {
        const props = this[size];
        Object.keys(props).forEach((prop) => {
          classList.push(
            prop !== 'span'
              ? `alt-col-${size}-${prop}-${props[prop]}`
              : `alt-col-${size}-${props[prop]}`,
          );
        });
      }
    });

    return h(this.tag, {
      class: ['alt-col', classList],
      style,
    }, this.$slots.default);
  },
};
</script>

<style lang="scss">
@import '../assets/scss/grid';

$--breakpoints: (
  'xs' : (max-width: $sm - 1),
  'sm' : (min-width: $sm),
  'md' : (min-width: $md),
  'lg' : (min-width: $lg),
  'xl' : (min-width: $xl)
);

@mixin res($key, $map: $--breakpoints) {
  @if map-has-key($map, $key) {
    @media only screen and #{inspect(map-get($map, $key))} {
      @content;
    }
  } @else {
    @warn "Undefeined points: `#{$map}`";
  }
}

[class*="alt-col-"] {
  float: left;
  box-sizing: border-box;
}

.alt-col-0 {
  display: none;
}

@for $i from 0 through 24 {
  .alt-col-#{$i} {
    width: (1 / 24 * $i * 100) * 1%;
  }

  .alt-col-offset-#{$i} {
    margin-left: (1 / 24 * $i * 100) * 1%;
  }

  .alt-col-pull-#{$i} {
    position: relative;
    right: (1 / 24 * $i * 100) * 1%;
  }

  .alt-col-push-#{$i} {
    position: relative;
    left: (1 / 24 * $i * 100) * 1%;
  }
}

@include res(xs) {
  .alt-col-xs-0 {
    display: none;
  }
  @for $i from 0 through 24 {
    .alt-col-xs-#{$i} {
      width: (1 / 24 * $i * 100) * 1%;
    }

    .alt-col-xs-offset-#{$i} {
      margin-left: (1 / 24 * $i * 100) * 1%;
    }

    .alt-col-xs-pull-#{$i} {
      position: relative;
      right: (1 / 24 * $i * 100) * 1%;
    }

    .alt-col-xs-push-#{$i} {
      position: relative;
      left: (1 / 24 * $i * 100) * 1%;
    }
  }
}

@include res(sm) {
  .alt-col-sm-0 {
    display: none;
  }
  @for $i from 0 through 24 {
    .alt-col-sm-#{$i} {
      width: (1 / 24 * $i * 100) * 1%;
    }

    .alt-col-sm-offset-#{$i} {
      margin-left: (1 / 24 * $i * 100) * 1%;
    }

    .alt-col-sm-pull-#{$i} {
      position: relative;
      right: (1 / 24 * $i * 100) * 1%;
    }

    .alt-col-sm-push-#{$i} {
      position: relative;
      left: (1 / 24 * $i * 100) * 1%;
    }
  }
}

@include res(md) {
  .alt-col-md-0 {
    display: none;
  }
  @for $i from 0 through 24 {
    .alt-col-md-#{$i} {
      width: (1 / 24 * $i * 100) * 1%;
    }

    .alt-col-md-offset-#{$i} {
      margin-left: (1 / 24 * $i * 100) * 1%;
    }

    .alt-col-md-pull-#{$i} {
      position: relative;
      right: (1 / 24 * $i * 100) * 1%;
    }

    .alt-col-md-push-#{$i} {
      position: relative;
      left: (1 / 24 * $i * 100) * 1%;
    }
  }
}

@include res(lg) {
  .alt-col-lg-0 {
    display: none;
  }
  @for $i from 0 through 24 {
    .alt-col-lg-#{$i} {
      width: (1 / 24 * $i * 100) * 1%;
    }

    .alt-col-lg-offset-#{$i} {
      margin-left: (1 / 24 * $i * 100) * 1%;
    }

    .alt-col-lg-pull-#{$i} {
      position: relative;
      right: (1 / 24 * $i * 100) * 1%;
    }

    .alt-col-lg-push-#{$i} {
      position: relative;
      left: (1 / 24 * $i * 100) * 1%;
    }
  }
}

@include res(xl) {
  .alt-col-xl-0 {
    display: none;
  }
  @for $i from 0 through 24 {
    .alt-col-xl-#{$i} {
      width: (1 / 24 * $i * 100) * 1%;
    }

    .alt-col-xl-offset-#{$i} {
      margin-left: (1 / 24 * $i * 100) * 1%;
    }

    .alt-col-xl-pull-#{$i} {
      position: relative;
      right: (1 / 24 * $i * 100) * 1%;
    }

    .alt-col-xl-push-#{$i} {
      position: relative;
      left: (1 / 24 * $i * 100) * 1%;
    }
  }
}
</style>



// WEBPACK FOOTER //
// app/src/components/GridCol.vue